import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Banknote,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  Headphones,
  Inbox,
  Loader2,
  LogOut,
  MessageSquare,
  Phone,
  Receipt,
  RefreshCw,
  Router,
  Search,
  Send,
  Ticket,
  UserRound,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchApp } from "../../services/apiService";

type CustomerRow = {
  id: string;
  secret_id?: string;
  portal_account_id?: string | null;
  name: string;
  phone: string;
  username: string;
  package: string;
  status: string;
  location: string;
  expiry: string;
  expiryRaw?: string | null;
};

type TicketRow = {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  message: string;
  created_at?: string;
};

type CustomerTicketRow = {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  message: string;
  status: string;
  is_outsourced?: boolean;
  resolved_at?: string | null;
  closed_at?: string | null;
  last_updated_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type InvoiceRow = {
  id: string;
  invoice_number: string;
  status: string;
  currency: string;
  total_amount_cents: number;
  paid_amount_cents: number;
  period_start?: string | null;
  period_end?: string | null;
  due_date?: string | null;
  created_at?: string | null;
};

type PaymentRow = {
  id: string;
  invoice_id?: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  external_reference?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
};

type ExtensionRow = {
  id: string;
  previous_access_expires_at?: string | null;
  new_access_expires_at?: string | null;
  reason?: string | null;
  created_at?: string | null;
};

type ActiveConnectionDetail = {
  id?: string | null;
  ".id"?: string | null;
  name?: string | null;
  service?: string | null;
  caller_id?: string | null;
  "caller-id"?: string | null;
  address?: string | null;
  uptime?: string | null;
  encoding?: string | null;
  session_id?: string | null;
  "session-id"?: string | null;
  limit_bytes_in?: string | number | null;
  "limit-bytes-in"?: string | number | null;
  limit_bytes_out?: string | number | null;
  "limit-bytes-out"?: string | number | null;
  radius?: string | boolean | null;
  comment?: string | null;
};

type ActiveConnectionResponse = {
  ok?: boolean;
  router_id?: string;
  router_name?: string;
  name?: string;
  active: boolean;
  message?: string;
  data?: ActiveConnectionDetail | null;
};

type CustomerDetail = {
  customer?: {
    id?: string | null;
    full_name?: string | null;
    phone_number?: string | null;
    email?: string | null;
    national_id?: string | null;
    address?: string | null;
    created_at?: string | null;
  } | null;
  secret?: {
    id?: string;
    username?: string;
    account_number?: string;
    phone_number?: string;
    email?: string;
    status?: boolean;
    amount_payable?: number;
    service_expires_at?: string | null;
    access_expires_at?: string | null;
    created_at?: string | null;
  } | null;
  router?: {
    id?: string | null;
    name?: string | null;
    ip_address?: string | null;
  } | null;
  plan?: {
    id?: string | null;
    name?: string | null;
    price?: number | null;
    speed?: string | null;
  } | null;
  billing_account?: {
    id?: string | null;
    account_type?: string | null;
    currency?: string | null;
    balance_cents?: number;
    is_active?: boolean;
  } | null;
  billing_group?: {
    id?: string | null;
    name?: string | null;
    payment_code?: string | null;
    amount_payable?: number | null;
    is_primary?: boolean;
  } | null;
  latest_invoice?: InvoiceRow | null;
  latest_payment?: PaymentRow | null;
  latest_ticket?: CustomerTicketRow | null;
  ticket_stats?: { total: number; open: number } | null;
  invoices?: InvoiceRow[];
  payments?: PaymentRow[];
  tickets?: CustomerTicketRow[];
  extensions?: ExtensionRow[];
};

type ExpiryTone = "critical" | "warning" | "healthy" | "unknown";
type CustomerStatusFilter = "all" | "active" | "inactive";
type DetailTab = "overview" | "billing" | "tickets" | "extensions";
type TicketQueueFilter = "all" | "open" | "resolved";
type ActiveView = "customers" | "tickets";

const OPEN_STATUSES = ["open", "assigned", "accepted", "in_progress"];
const RESOLVED_STATUSES = [
  "closed",
  "customer_confirmed",
  "technician_completed",
];

const CUSTOMER_STATUS_FILTERS: Array<{
  key: CustomerStatusFilter;
  label: string;
}> = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
];

const TICKET_QUEUE_FILTERS: Array<{ key: TicketQueueFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
];

const DETAIL_TABS: Array<{ key: DetailTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "billing", label: "Billing & payments" },
  { key: "tickets", label: "Tickets" },
  { key: "extensions", label: "Extensions" },
];

const CATEGORY_OPTIONS = [
  { value: "NO_INTERNET", label: "No internet" },
  { value: "SLOW_INTERNET", label: "Slow internet" },
  { value: "PAYMENT_ISSUE", label: "Payment issue" },
  { value: "ROUTER_ISSUE", label: "Router issue" },
  { value: "PACKAGE_CHANGE", label: "Package change" },
  { value: "OTHER", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", tone: "slate" },
  { value: "medium", label: "Medium", tone: "sky" },
  { value: "high", label: "High", tone: "amber" },
  { value: "urgent", label: "Urgent", tone: "rose" },
];

const PRIORITY_TONE_CLASSES: Record<string, string> = {
  slate: "border-slate-900 bg-slate-900 text-white",
  sky: "border-sky-600 bg-sky-600 text-white",
  amber: "border-amber-500 bg-amber-500 text-white",
  rose: "border-rose-600 bg-rose-600 text-white",
};

const PRIORITY_BADGE_CLASSES: Record<string, string> = {
  low: "border-slate-200 bg-slate-50 text-slate-600",
  medium: "border-sky-200 bg-sky-50 text-sky-700",
  high: "border-amber-200 bg-amber-50 text-amber-700",
  urgent: "border-rose-200 bg-rose-50 text-rose-700",
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  open: "border-sky-200 bg-sky-50 text-sky-700",
  assigned: "border-violet-200 bg-violet-50 text-violet-700",
  accepted: "border-violet-200 bg-violet-50 text-violet-700",
  in_progress: "border-amber-200 bg-amber-50 text-amber-700",
  technician_completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  customer_confirmed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  closed: "border-slate-200 bg-slate-100 text-slate-500",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  pending: "border-amber-200 bg-amber-50 text-amber-700",
  failed: "border-rose-200 bg-rose-50 text-rose-700",
  expired: "border-rose-200 bg-rose-50 text-rose-700",
};

const TONE_STYLES: Record<ExpiryTone, { dot: string; chip: string }> = {
  critical: {
    dot: "bg-rose-500",
    chip: "border-rose-200 bg-rose-50 text-rose-700",
  },
  warning: {
    dot: "bg-amber-500",
    chip: "border-amber-200 bg-amber-50 text-amber-700",
  },
  healthy: {
    dot: "bg-emerald-500",
    chip: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  unknown: {
    dot: "bg-slate-300",
    chip: "border-slate-200 bg-slate-50 text-slate-500",
  },
};

function useDebouncedValue<T>(value: T, delay = 450): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export default function CustomerCareDashboard() {
  const navigate = useNavigate();

  // Top-level navigation: keeps the customer workspace and the system-wide
  // ticket queue as two clearly separated destinations instead of stacking
  // the full ticket queue on top of the customer workspace.
  const [activeView, setActiveView] = useState<ActiveView>("customers");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 450);

  const [statusFilter, setStatusFilter] = useState<CustomerStatusFilter>("all");

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerTotalPages, setCustomerTotalPages] = useState(1);
  const [customerLoading, setCustomerLoading] = useState(false);

  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [ticketQueueFilter, setTicketQueueFilter] =
    useState<TicketQueueFilter>("all");

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRow | null>(
    null,
  );
  const [customerDetail, setCustomerDetail] = useState<CustomerDetail | null>(
    null,
  );
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");

  const [activeName, setActiveName] = useState("");
  const [activeLookupLoading, setActiveLookupLoading] = useState(false);
  const [activeConnectionResult, setActiveConnectionResult] =
    useState<ActiveConnectionResponse | null>(null);

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("NO_INTERNET");
  const [priority, setPriority] = useState("medium");
  const [workText, setWorkText] = useState("");

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkActiveConnection = async (
    routerId: string | null | undefined,
    nameToCheck: string | number | null | undefined,
    showErrors = true,
  ) => {
    const name = (nameToCheck ?? "").toString().trim();

    if (!routerId || !name) {
      if (showErrors) {
        setError("Router and PPPoE username are required to check connection.");
      }

      return;
    }

    setActiveLookupLoading(true);

    try {
      const params = new URLSearchParams({ router_id: routerId, name });

      const res = await fetchApp<ActiveConnectionResponse>(
        `/secrets/active/detail?${params.toString()}`,
      );

      setActiveConnectionResult(res);
    } catch (err: any) {
      const message = err?.message || "Failed to check active connection.";

      setActiveConnectionResult({ active: false, message, data: null });

      if (showErrors) {
        setError(message);
      }
    } finally {
      setActiveLookupLoading(false);
    }
  };

  const loadCustomerDetail = async (customer: CustomerRow) => {
    setSelectedCustomer(customer);
    setCustomerDetail(null);
    setDetailTab("overview");
    setActiveConnectionResult(null);

    try {
      setDetailLoading(true);
      setError(null);

      const data = await fetchApp<CustomerDetail>(
        `/customer-care/customers/${customer.secret_id || customer.id}`,
      );

      setCustomerDetail(data);

      const nameToCheck =
        data.secret?.account_number ||
        data.secret?.username ||
        customer.username;
      setActiveName(String(nameToCheck || ""));

      // Auto-check the live PPPoE connection as soon as customer details load.
      if (data.router?.id && nameToCheck) {
        await checkActiveConnection(data.router.id, nameToCheck, false);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load customer details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCheckActiveConnection = async () => {
    if (!customerDetail) return;
    await checkActiveConnection(customerDetail.router?.id, activeName, true);
  };

  const loadCustomers = async (
    pageToLoad = 1,
    q = debouncedSearch,
    append = false,
    statusValue = statusFilter,
  ) => {
    try {
      setCustomerLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set("page", String(pageToLoad));
      params.set("page_size", "20");

      if (q.trim()) {
        params.set("q", q.trim());
      }

      params.set("status", statusValue);

      const res = await fetchApp<any>(
        `/customers/summary?${params.toString()}`,
      );
      const items = Array.isArray(res) ? res : res.items || [];

      const normalizedCustomers: CustomerRow[] = items.map((c: any) => ({
        id: String(c.secret_id || c.id),
        secret_id: String(c.secret_id || c.id),
        portal_account_id: c.portal_account_id || null,
        name: c.customer_name || c.username || "Unknown Customer",
        phone: c.phone_number || "-",
        username: c.username || "-",
        package: c.plan_name || c.plan?.name || "No Plan",
        status: c.status ? "Active" : "Inactive",
        location: c.location_name || c.location || c.router_name || "-",
        expiry: formatDate(c.service_expires_at),
        expiryRaw: c.service_expires_at || null,
      }));

      setCustomers((prev) =>
        append ? [...prev, ...normalizedCustomers] : normalizedCustomers,
      );

      setCustomerPage(Number(res.page || pageToLoad));
      setCustomerTotal(Number(res.total || normalizedCustomers.length));
      setCustomerTotalPages(Number(res.total_pages || 1));

      if (!append) {
        const firstCustomer = normalizedCustomers[0] || null;
        setSelectedCustomer(firstCustomer);

        if (firstCustomer) {
          await loadCustomerDetail(firstCustomer);
        } else {
          setCustomerDetail(null);
        }
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load customers.");
    } finally {
      setCustomerLoading(false);
    }
  };

  const loadTickets = async () => {
    const ticketsRes = await fetchApp<any>(
      "/customer-care/tickets?page=1&page_size=20",
    );

    const ticketItems = Array.isArray(ticketsRes)
      ? ticketsRes
      : ticketsRes.items || [];

    setTickets(ticketItems);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      await Promise.all([
        loadTickets(),
        loadCustomers(1, debouncedSearch, false, statusFilter),
      ]);
    } catch (err: any) {
      setError(err?.message || "Failed to load support data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets().catch((err: any) => {
      setError(err?.message || "Failed to load tickets.");
    });
  }, []);

  useEffect(() => {
    loadCustomers(1, debouncedSearch, false, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  const filteredQueueTickets = useMemo(() => {
    if (ticketQueueFilter === "all") return tickets;

    return tickets.filter((ticket) => {
      const key = String(ticket.status).toLowerCase();
      if (ticketQueueFilter === "open") return OPEN_STATUSES.includes(key);
      return RESOLVED_STATUSES.includes(key);
    });
  }, [tickets, ticketQueueFilter]);

  const openTickets = tickets.filter((t) =>
    OPEN_STATUSES.includes(String(t.status).toLowerCase()),
  ).length;

  const resolvedTickets = tickets.filter((t) =>
    RESOLVED_STATUSES.includes(String(t.status).toLowerCase()),
  ).length;

  const expiredCount = customers.filter(
    (c) => getExpiryHealth(c.expiryRaw).tone === "critical",
  ).length;

  const expiringSoonCount = customers.filter(
    (c) => getExpiryHealth(c.expiryRaw).tone === "warning",
  ).length;

  const createTicket = async () => {
    if (!selectedCustomer) {
      setError("Select a customer first.");
      return;
    }

    if (!subject.trim() || !workText.trim()) {
      setError("Enter ticket subject and details.");
      return;
    }

    try {
      setCreating(true);
      setError(null);

      await fetchApp("/customer-care/tickets", {
        method: "POST",
        body: JSON.stringify({
          portal_account_id: selectedCustomer.portal_account_id || null,
          subject: subject.trim(),
          secret_id: selectedCustomer.secret_id || null,
          category,
          priority,
          message: `${workText.trim()}\n\nCustomer: ${selectedCustomer.name}\nPhone: ${selectedCustomer.phone}\nPPPoE: ${selectedCustomer.username}`,
          latitude: null,
          longitude: null,
          location_name: selectedCustomer.location,
        }),
      });

      setSubject("");
      setWorkText("");
      await loadTickets();
      await loadCustomerDetail(selectedCustomer);
    } catch (err: any) {
      setError(err?.message || "Failed to create ticket.");
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("userRole");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  const detailCustomer = customerDetail?.customer;
  const detailSecret = customerDetail?.secret;
  const detailBilling = customerDetail?.billing_account;
  const detailGroup = customerDetail?.billing_group;
  const latestTicket = customerDetail?.latest_ticket;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between px-5 py-4">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-600">
              AAMX Customer Care
            </p>
            <h1 className="mt-0.5 text-xl font-black tracking-tight text-slate-950">
              Support Desk
            </h1>
            <p className="mt-0.5 text-xs font-semibold text-slate-400">
              {customerTotal} customers found • {openTickets} open tickets •{" "}
              {resolvedTickets} resolved
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadData}
              disabled={loading || customerLoading}
              className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
            >
              {loading || customerLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-10 items-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Primary navigation: switches the whole workspace between the
           customer directory and the system-wide ticket queue. */}
        <div className="mx-auto max-w-[1800px] px-5 pb-3">
          <nav className="flex items-center gap-1.5 rounded-2xl bg-slate-100 p-1.5">
            <button
              type="button"
              onClick={() => setActiveView("customers")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition sm:flex-none sm:px-6 ${
                activeView === "customers"
                  ? "bg-white text-slate-950 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <UserRound className="h-4 w-4" />
              Customer Directory
            </button>

            <button
              type="button"
              onClick={() => setActiveView("tickets")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition sm:flex-none sm:px-6 ${
                activeView === "tickets"
                  ? "bg-white text-slate-950 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Inbox className="h-4 w-4" />
              Ticket Queue
              {openTickets > 0 && (
                <span
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-black ${
                    activeView === "tickets"
                      ? "bg-sky-600 text-white"
                      : "bg-sky-100 text-sky-700"
                  }`}
                >
                  {openTickets}
                </span>
              )}
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1800px] px-5 py-5">
        {error && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="shrink-0 rounded-full p-1 text-rose-500 transition hover:bg-rose-100"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <section className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard
            icon={<Ticket className="h-5 w-5" />}
            label="Open Tickets"
            value={openTickets}
            tone="sky"
          />
          <StatCard
            icon={<UserRound className="h-5 w-5" />}
            label="Customers Found"
            value={customerTotal}
            tone="slate"
          />
          <StatCard
            icon={<AlertTriangle className="h-5 w-5" />}
            label="Expired Loaded"
            value={expiredCount}
            tone="rose"
          />
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            label="Expiring Loaded"
            value={expiringSoonCount}
            tone="amber"
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Resolved"
            value={resolvedTickets}
            tone="emerald"
          />
        </section>

        {activeView === "tickets" ? (
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                  <Inbox className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-black text-slate-950">
                    All System Tickets
                  </h3>
                  <p className="text-xs font-semibold text-slate-400">
                    {tickets.length} latest tickets across customers
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                {TICKET_QUEUE_FILTERS.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => setTicketQueueFilter(filter.key)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                      ticketQueueFilter === filter.key
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <EmptyMini text="Loading tickets..." />
            ) : filteredQueueTickets.length === 0 ? (
              <EmptyMini text="No tickets match this filter." />
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {filteredQueueTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-slate-300"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                        {ticket.ticket_number}
                      </p>
                      <StatusPill status={ticket.status} />
                    </div>

                    <p className="mt-2 truncate text-sm font-black text-slate-950">
                      {ticket.subject}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <CategoryTag category={ticket.category} />
                      <PriorityPill priority={ticket.priority} />
                    </div>

                    <p className="mt-2 text-xs font-medium text-slate-400">
                      Created {formatDate(ticket.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="grid gap-5 xl:grid-cols-[390px_minmax(0,1fr)]">
            <aside className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-white p-4 pb-3">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search all customers from server..."
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-10 pr-9 text-sm font-semibold text-slate-700 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                  />

                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-1.5 overflow-x-auto pb-0.5">
                  {CUSTOMER_STATUS_FILTERS.map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setStatusFilter(filter.key)}
                      className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                        statusFilter === filter.key
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                <p className="mt-3 text-xs font-bold text-slate-400">
                  Showing {customers.length} of {customerTotal}
                </p>
              </div>

              <div className="h-[calc(100vh-340px)] min-h-[500px] overflow-y-auto">
                {customerLoading && customers.length === 0 ? (
                  <EmptyState
                    icon={<Loader2 className="h-5 w-5 animate-spin" />}
                    title="Searching customers"
                    message="Please wait..."
                  />
                ) : customers.length === 0 ? (
                  <EmptyState
                    icon={<Search className="h-5 w-5" />}
                    title="No customers found"
                    message="Try another name, phone, username, account number or router."
                    action={
                      search || statusFilter !== "all" ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSearch("");
                            setStatusFilter("all");
                          }}
                          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
                        >
                          <X className="h-3.5 w-3.5" />
                          Clear filters
                        </button>
                      ) : undefined
                    }
                  />
                ) : (
                  <>
                    {customers.map((customer) => {
                      const active = selectedCustomer?.id === customer.id;
                      const { tone } = getExpiryHealth(customer.expiryRaw);

                      return (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => loadCustomerDetail(customer)}
                          className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3.5 text-left transition ${
                            active ? "bg-emerald-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div
                            className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-black uppercase ${
                              active
                                ? "bg-emerald-600 text-white"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            {customer.name.charAt(0)}
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${TONE_STYLES[tone].dot}`}
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-sm font-black text-slate-950">
                                {customer.name}
                              </p>
                              <ExpiryChip
                                raw={customer.expiryRaw}
                                fallback={customer.expiry}
                              />
                            </div>

                            <p className="mt-1 truncate text-xs font-semibold text-slate-500">
                              {customer.phone} • {customer.username}
                            </p>

                            <p className="mt-0.5 truncate text-xs font-medium text-slate-400">
                              {customer.package} • {customer.location}
                            </p>
                          </div>
                        </button>
                      );
                    })}

                    {customerPage < customerTotalPages && (
                      <div className="border-t border-slate-100 p-3">
                        <button
                          type="button"
                          disabled={customerLoading}
                          onClick={() =>
                            loadCustomers(
                              customerPage + 1,
                              debouncedSearch,
                              true,
                              statusFilter,
                            )
                          }
                          className="flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                        >
                          {customerLoading && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          Load more customers
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>

            <section className="space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                {detailLoading ? (
                  <EmptyState
                    icon={<Loader2 className="h-5 w-5 animate-spin" />}
                    title="Loading customer details"
                    message="Fetching billing, payments and account information."
                  />
                ) : selectedCustomer ? (
                  <>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex min-w-0 items-start gap-4">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-emerald-100 text-2xl font-black uppercase text-emerald-700">
                          {selectedCustomer.name.charAt(0)}
                        </div>

                        <div className="min-w-0">
                          <h2 className="truncate text-2xl font-black tracking-tight text-slate-950">
                            {detailCustomer?.full_name || selectedCustomer.name}
                          </h2>

                          <p className="mt-1 text-sm font-semibold text-slate-500">
                            {detailSecret?.username ||
                              selectedCustomer.username}{" "}
                            •{" "}
                            {detailSecret?.phone_number ||
                              selectedCustomer.phone}
                          </p>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Badge>
                              {detailSecret?.status ? "Active" : "Inactive"}
                            </Badge>
                            <Badge>
                              {customerDetail?.plan?.name ||
                                selectedCustomer.package}
                            </Badge>
                            <Badge>
                              {customerDetail?.router?.name ||
                                selectedCustomer.location}
                            </Badge>
                            <ActiveConnectionBadge
                              loading={activeLookupLoading}
                              result={activeConnectionResult}
                            />
                            <ExpiryChip
                              raw={
                                detailSecret?.service_expires_at ??
                                selectedCustomer.expiryRaw
                              }
                              fallback={selectedCustomer.expiry}
                            />
                          </div>

                          {latestTicket && (
                            <p className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
                              <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                              Last ticket {latestTicket.ticket_number}
                              <StatusPill status={latestTicket.status} />
                            </p>
                          )}
                        </div>
                      </div>

                      <a
                        href={`tel:${detailSecret?.phone_number || selectedCustomer.phone}`}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-black text-white transition hover:bg-slate-800"
                      >
                        <Phone className="h-4 w-4" />
                        Call
                      </a>
                    </div>

                    <div className="mt-5 flex items-center gap-1 rounded-2xl bg-slate-100 p-1">
                      {DETAIL_TABS.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setDetailTab(tab.key)}
                          className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                            detailTab === tab.key
                              ? "bg-white text-slate-950 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <EmptyState
                    icon={<UserRound className="h-5 w-5" />}
                    title="Select a customer"
                    message="Customer details will appear here."
                  />
                )}
              </div>

              {customerDetail && detailTab === "overview" && (
                <div className="space-y-5">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <InfoCard
                      icon={<UserRound className="h-4 w-4" />}
                      label="Customer"
                      items={[
                        [
                          "Full name",
                          detailCustomer?.full_name || selectedCustomer?.name,
                        ],
                        [
                          "Phone",
                          detailCustomer?.phone_number ||
                            selectedCustomer?.phone,
                        ],
                        [
                          "Email",
                          detailCustomer?.email || detailSecret?.email || "-",
                        ],
                        ["National ID", detailCustomer?.national_id || "-"],
                        ["Address", detailCustomer?.address || "-"],
                      ]}
                    />

                    <InfoCard
                      icon={<Router className="h-4 w-4" />}
                      label="PPPoE Account"
                      items={[
                        [
                          "Username",
                          detailSecret?.username || selectedCustomer?.username,
                        ],
                        ["Account no.", detailSecret?.account_number || "-"],
                        [
                          "Status",
                          detailSecret?.status ? "Active" : "Inactive",
                        ],
                        [
                          "Amount payable",
                          formatKES(detailSecret?.amount_payable),
                        ],
                        [
                          "Access expiry",
                          formatDate(detailSecret?.access_expires_at),
                        ],
                      ]}
                    />

                    <InfoCard
                      icon={<Router className="h-4 w-4" />}
                      label="Router & Plan"
                      items={[
                        ["Router", customerDetail.router?.name || "-"],
                        ["Router IP", customerDetail.router?.ip_address || "-"],
                        ["Plan", customerDetail.plan?.name || "-"],
                        ["Speed", customerDetail.plan?.speed || "-"],
                        [
                          "Service expiry",
                          formatDate(detailSecret?.service_expires_at),
                        ],
                      ]}
                    />
                  </div>

                  <DataPanel
                    title="Live Connection Status"
                    icon={<Activity className="h-4 w-4" />}
                  >
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                            PPPoE Name / Username
                          </p>
                          <ActiveConnectionBadge
                            loading={activeLookupLoading}
                            result={activeConnectionResult}
                          />
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row">
                          <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                              value={activeName}
                              onChange={(e) => {
                                setActiveName(e.target.value);
                                setActiveConnectionResult(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleCheckActiveConnection();
                                }
                              }}
                              placeholder="Enter PPPoE username"
                              className="h-10 w-full rounded-2xl border border-slate-300 bg-white pl-9 pr-3 text-sm font-mono font-bold text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={handleCheckActiveConnection}
                            disabled={activeLookupLoading}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-black text-white transition hover:bg-slate-800 disabled:opacity-60"
                          >
                            {activeLookupLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Activity className="h-4 w-4" />
                            )}
                            Recheck
                          </button>
                        </div>

                        <p className="mt-2 text-xs font-semibold text-slate-400">
                          Router:{" "}
                          {customerDetail.router?.name || "Assigned router"}
                        </p>
                      </div>

                      {activeLookupLoading ? (
                        <EmptyMini text="Checking active PPPoE session..." />
                      ) : activeConnectionResult?.active &&
                        activeConnectionResult.data ? (
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          <DetailMini
                            label="Client Address"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "address",
                            )}
                          />
                          <DetailMini
                            label="Uptime"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "uptime",
                            )}
                          />
                          <DetailMini
                            label="Caller ID / MAC"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "caller_id",
                              "caller-id",
                            )}
                          />
                          <DetailMini
                            label="Service"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "service",
                            )}
                          />
                          <DetailMini
                            label="Session ID"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "session_id",
                              "session-id",
                            )}
                          />
                          <DetailMini
                            label="Radius"
                            value={getConnectionValue(
                              activeConnectionResult.data,
                              "radius",
                            )}
                          />
                        </div>
                      ) : (
                        <EmptyMini
                          text={
                            activeConnectionResult?.message ||
                            `No active PPPoE connection found${
                              activeName ? ` for ${activeName}` : ""
                            }.`
                          }
                        />
                      )}
                    </div>
                  </DataPanel>
                </div>
              )}

              {customerDetail && detailTab === "billing" && (
                <div className="space-y-5">
                  <div className="grid gap-3 md:grid-cols-3">
                    <SummaryBox
                      icon={<Wallet className="h-5 w-5" />}
                      label="Current Balance"
                      value={formatCents(
                        customerDetail.billing_account?.balance_cents,
                      )}
                      sub={
                        customerDetail.billing_account?.is_active
                          ? "Billing active"
                          : "Billing inactive"
                      }
                    />
                    <SummaryBox
                      icon={<Receipt className="h-5 w-5" />}
                      label="Last Invoice"
                      value={
                        customerDetail.latest_invoice?.invoice_number || "-"
                      }
                      sub={`${customerDetail.latest_invoice?.status || "No invoice"} • ${formatCents(
                        customerDetail.latest_invoice?.total_amount_cents,
                      )}`}
                    />
                    <SummaryBox
                      icon={<CreditCard className="h-5 w-5" />}
                      label="Last Payment"
                      value={formatCents(
                        customerDetail.latest_payment?.amount_cents,
                      )}
                      sub={
                        customerDetail.latest_payment?.status || "No payment"
                      }
                    />
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <InfoCard
                      icon={<Wallet className="h-4 w-4" />}
                      label="Billing Account"
                      items={[
                        ["Type", detailBilling?.account_type || "-"],
                        ["Currency", detailBilling?.currency || "KES"],
                        ["Balance", formatCents(detailBilling?.balance_cents)],
                        [
                          "Billing status",
                          detailBilling?.is_active ? "Active" : "Inactive",
                        ],
                      ]}
                    />

                    <InfoCard
                      icon={<Banknote className="h-4 w-4" />}
                      label="Billing Group"
                      items={[
                        ["Group", detailGroup?.name || "-"],
                        ["Payment code", detailGroup?.payment_code || "-"],
                        ["Amount", formatKES(detailGroup?.amount_payable)],
                        ["Primary", detailGroup?.is_primary ? "Yes" : "No"],
                      ]}
                    />
                  </div>

                  <DataPanel
                    title="Recent Invoices"
                    icon={<Receipt className="h-4 w-4" />}
                  >
                    {customerDetail.invoices?.length ? (
                      <div className="space-y-3">
                        {customerDetail.invoices.map((invoice) => (
                          <RecordRow
                            key={invoice.id}
                            title={invoice.invoice_number}
                            badge={<StatusPill status={invoice.status} />}
                            line1={`Total: ${formatCents(invoice.total_amount_cents)} • Paid: ${formatCents(
                              invoice.paid_amount_cents,
                            )}`}
                            line2={`Due: ${formatDate(invoice.due_date)} • Created: ${formatDate(invoice.created_at)}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyMini text="No invoices found." />
                    )}
                  </DataPanel>

                  <DataPanel
                    title="Recent Payments"
                    icon={<CreditCard className="h-4 w-4" />}
                  >
                    {customerDetail.payments?.length ? (
                      <div className="space-y-3">
                        {customerDetail.payments.map((payment) => (
                          <RecordRow
                            key={payment.id}
                            title={formatCents(payment.amount_cents)}
                            badge={<StatusPill status={payment.status} />}
                            line1={`Reference: ${payment.external_reference || "-"}`}
                            line2={`Paid: ${formatDateTime(payment.paid_at || payment.created_at)}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyMini text="No payments found." />
                    )}
                  </DataPanel>
                </div>
              )}

              {customerDetail && detailTab === "tickets" && (
                <div className="space-y-5">
                  <div className="grid gap-3 md:grid-cols-2">
                    <SummaryBox
                      icon={<Ticket className="h-5 w-5" />}
                      label="Total Tickets"
                      value={
                        customerDetail.ticket_stats?.total ??
                        customerDetail.tickets?.length ??
                        0
                      }
                      sub="Lifetime tickets raised"
                    />
                    <SummaryBox
                      icon={<AlertTriangle className="h-5 w-5" />}
                      label="Open Tickets"
                      value={customerDetail.ticket_stats?.open ?? 0}
                      sub="Currently unresolved"
                    />
                  </div>

                  <DataPanel
                    title="Ticket History"
                    icon={<Ticket className="h-4 w-4" />}
                  >
                    {customerDetail.tickets?.length ? (
                      <div className="space-y-3">
                        {customerDetail.tickets.map((ticket) => (
                          <RecordRow
                            key={ticket.id}
                            title={`${ticket.ticket_number} • ${ticket.subject}`}
                            badge={<StatusPill status={ticket.status} />}
                            line1={
                              <span className="flex flex-wrap items-center gap-1.5">
                                <CategoryTag category={ticket.category} />
                                <PriorityPill priority={ticket.priority} />
                              </span>
                            }
                            line2={`Created ${formatDateTime(ticket.created_at)}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyMini text="No tickets raised by this customer yet." />
                    )}
                  </DataPanel>

                  {/* Create ticket now lives only inside the Tickets tab, scoped
                     to whichever customer is selected. */}
                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4">
                      <div className="flex items-center gap-2">
                        <Headphones className="h-5 w-5 text-emerald-600" />
                        <h3 className="font-black text-slate-950">
                          Create Support Ticket
                        </h3>
                      </div>
                      <p className="mt-1 text-sm font-medium text-slate-500">
                        Admin will be notified to assign this issue.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Ticket subject"
                        className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                      />

                      <div>
                        <p className="mb-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Category
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {CATEGORY_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setCategory(opt.value)}
                              className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                                category === opt.value
                                  ? "border-slate-900 bg-slate-900 text-white"
                                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Priority
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {PRIORITY_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setPriority(opt.value)}
                              className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                                priority === opt.value
                                  ? PRIORITY_TONE_CLASSES[opt.tone]
                                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <textarea
                        rows={5}
                        value={workText}
                        onChange={(e) => setWorkText(e.target.value)}
                        placeholder="Write what the customer reported, what you checked, and any follow-up needed..."
                        className="w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 p-4 text-sm font-medium outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                      />

                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          disabled={creating}
                          onClick={createTicket}
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 text-sm font-black text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-600 disabled:opacity-60"
                        >
                          {creating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          Create Ticket
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setSubject("");
                            setWorkText("");
                          }}
                          className="h-11 rounded-2xl border border-slate-300 bg-white px-5 text-sm font-black text-slate-700 transition hover:bg-slate-50"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {customerDetail && detailTab === "extensions" && (
                <DataPanel
                  title="Access Extensions"
                  icon={<CalendarDays className="h-4 w-4" />}
                >
                  {customerDetail.extensions?.length ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      {customerDetail.extensions.map((extension) => (
                        <RecordRow
                          key={extension.id}
                          title={`Extended to ${formatDate(extension.new_access_expires_at)}`}
                          badge={<Badge>Extension</Badge>}
                          line1={`Previous: ${formatDate(extension.previous_access_expires_at)}`}
                          line2={extension.reason || "No reason added"}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyMini text="No access extensions found." />
                  )}
                </DataPanel>
              )}
            </section>
          </section>
        )}
      </main>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone = "emerald",
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: "emerald" | "sky" | "amber" | "rose" | "slate";
}) {
  const toneClasses: Record<string, string> = {
    emerald: "bg-emerald-100 text-emerald-700",
    sky: "bg-sky-100 text-sky-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-black tracking-tight text-slate-950">
            {value}
          </p>
          <p className="mt-1 text-[11px] font-black uppercase tracking-widest text-slate-500">
            {label}
          </p>
        </div>
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${toneClasses[tone]}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function ExpiryChip({
  raw,
  fallback,
}: {
  raw?: string | null;
  fallback?: string;
}) {
  const { tone, days } = getExpiryHealth(raw);
  const styles = TONE_STYLES[tone];

  let label = fallback || "-";
  if (tone === "critical") label = `Expired ${Math.abs(days || 0)}d ago`;
  if (tone === "warning")
    label = days === 0 ? "Expires today" : `${days}d left`;
  if (tone === "healthy") label = formatDate(raw);

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${styles.chip}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
      {label}
    </span>
  );
}

function StatusPill({ status }: { status?: string | null }) {
  const key = String(status || "").toLowerCase();
  const classes =
    STATUS_BADGE_CLASSES[key] || "border-slate-200 bg-slate-50 text-slate-600";

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[11px] font-black ${classes}`}
    >
      {prettifyLabel(status)}
    </span>
  );
}

function ActiveConnectionBadge({
  loading,
  result,
}: {
  loading?: boolean;
  result?: ActiveConnectionResponse | null;
}) {
  if (loading) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-black text-sky-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking
      </span>
    );
  }

  if (!result) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-black text-slate-500">
        Not checked
      </span>
    );
  }

  if (result.active) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        Online
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-black text-rose-700">
      <XCircle className="h-3 w-3" />
      Offline
    </span>
  );
}

function PriorityPill({ priority }: { priority?: string | null }) {
  const key = String(priority || "").toLowerCase();
  const classes =
    PRIORITY_BADGE_CLASSES[key] ||
    "border-slate-200 bg-slate-50 text-slate-600";

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[11px] font-black ${classes}`}
    >
      {prettifyLabel(priority)}
    </span>
  );
}

function CategoryTag({ category }: { category?: string | null }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-500">
      {prettifyLabel(category)}
    </span>
  );
}

function InfoCard({
  icon,
  label,
  items,
}: {
  icon: React.ReactNode;
  label: string;
  items: Array<[string, React.ReactNode]>;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-emerald-700">
          {icon}
        </div>
        <p className="text-sm font-black text-slate-950">{label}</p>
      </div>

      <div className="space-y-2">
        {items.map(([key, value]) => (
          <div key={key} className="flex items-start justify-between gap-3">
            <p className="shrink-0 text-xs font-bold text-slate-500">{key}</p>
            <p className="min-w-0 text-right text-xs font-black text-slate-800">
              {value || "-"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailMini({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p className="mt-1 break-words text-xs font-black text-slate-800">
        {value || "-"}
      </p>
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
      {children}
    </span>
  );
}

function EmptyState({
  icon,
  title,
  message,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-[160px] items-center justify-center p-6 text-center">
      <div>
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
          {icon}
        </div>
        <p className="mt-3 text-sm font-black text-slate-800">{title}</p>
        <p className="mt-1 text-xs font-medium text-slate-500">{message}</p>
        {action}
      </div>
    </div>
  );
}

function SummaryBox({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
        {icon}
      </div>
      <p className="truncate text-lg font-black text-slate-950">
        {value || "-"}
      </p>
      <p className="mt-1 text-[11px] font-black uppercase tracking-widest text-slate-500">
        {label}
      </p>
      {sub && (
        <p className="mt-1 truncate text-xs font-semibold text-slate-500">
          {sub}
        </p>
      )}
    </div>
  );
}

function DataPanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-emerald-700">
          {icon}
        </div>
        <h3 className="font-black text-slate-950">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function RecordRow({
  title,
  badge,
  line1,
  line2,
}: {
  title: React.ReactNode;
  badge?: React.ReactNode;
  line1?: React.ReactNode;
  line2?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-slate-300">
      <div className="flex items-start justify-between gap-3">
        <p className="font-black text-slate-950">{title}</p>
        {badge}
      </div>
      {line1 && (
        <div className="mt-2 text-sm font-semibold text-slate-600">{line1}</div>
      )}
      {line2 && (
        <p className="mt-1 text-xs font-medium text-slate-500">{line2}</p>
      )}
    </div>
  );
}

function EmptyMini({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
      {text}
    </div>
  );
}

function getConnectionValue(
  data: ActiveConnectionDetail | null | undefined,
  ...keys: (keyof ActiveConnectionDetail)[]
) {
  if (!data) return "-";

  for (const key of keys) {
    const value = data[key];

    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }

  return "-";
}

function getExpiryHealth(raw?: string | null): {
  tone: ExpiryTone;
  days: number | null;
} {
  if (!raw) return { tone: "unknown", days: null };

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return { tone: "unknown", days: null };

  const diffMs = date.getTime() - Date.now();
  const days = Math.ceil(diffMs / 86400000);

  if (days < 0) return { tone: "critical", days };
  if (days <= 7) return { tone: "warning", days };
  return { tone: "healthy", days };
}

function prettifyLabel(value?: string | null) {
  if (!value) return "-";

  return value
    .toLowerCase()
    .split("_")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

function formatDate(value?: string | null) {
  if (!value || value === "-") return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCents(value?: number | null) {
  const amount = Number(value || 0) / 100;
  return `KES ${new Intl.NumberFormat("en-KE").format(amount)}`;
}

function formatKES(value?: number | null) {
  return `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0))}`;
}
